import axios from 'axios'
import { reverse } from 'named-urls'
import type { ReactNode } from 'react'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Toasts, useModalContext } from '@fourthwall/components'
import { getDefaultErrorMessage } from '@fourthwall/utils/dist/queryClient'
import { routing } from '@utils/routing'

import { useCancellationOffers } from '../../hooks/useCancellationOffers'
import { useOrderIdParam } from '../../hooks/useOrderIdParam'
import { useOrderGroupedByShipmentsQuery } from '../../queries/useOrderGroupedByShipmentsQuery'
import type { ExpectedCostSplit } from '../OrderRefund/modules/OrderRefundConfirmModal'
import type { SelectableOffer } from './OrderCancel.types'
import { OrderCancelView } from './OrderCancelView'
import { OrderCancelConfirmModal } from './modules/OrderCancelConfirmModal'
import { useCalculateCancelRefundsQuery } from './queries/useCalculateCancelRefundsQuery'
import { useCancelOrderMutation } from './queries/useCancelOrderMutation'
import { handleCalculateRefundsError } from './utils/handleCalculateRefundsError'

export const OrderCancelContainer = () => {
  const navigate = useNavigate()
  const { open } = useModalContext()
  const { orderId } = useOrderIdParam()
  const { orderGroupedByShipmentsQuery } = useOrderGroupedByShipmentsQuery([{ orderId }])

  const [selectableOffers, setSelectableOffers] = useState<SelectableOffer[]>()
  const [shouldRestock, setShouldRestock] = useState(true)
  const [itemsToCancel, setItemsToCancel] = useState<{ variantId: string; quantity: number }[]>([])
  const {
    cancellableOffers,
    nonCancellableOffers,
    partiallyCancellable,
    orderCancellationQuery,
    invalidateOrderCancellationQuery,
  } = useCancellationOffers(orderId, orderGroupedByShipmentsQuery.data?.offers)
  const { calculateCancelRefundsQuery } = useCalculateCancelRefundsQuery(
    [orderId, { itemsToCancel }],
    { retry: false, keepPreviousData: true, onError: handleCalculateRefundsError },
  )
  const { cancelOrderMutation } = useCancelOrderMutation({ onError: undefined })
  const calculateCancelRefunds = calculateCancelRefundsQuery.data

  useEffect(() => {
    if (cancellableOffers) {
      setSelectableOffers(
        cancellableOffers?.map((offer) => ({
          ...offer,
          selected: false,
          selectedQuantity: offer.quantity,
        })),
      )
    }
  }, [cancellableOffers])

  useEffect(() => {
    if (!selectableOffers) {
      setItemsToCancel([])

      return
    }

    const itemsToCancel = selectableOffers
      .filter((selectableOffer) => selectableOffer.selected)
      .map(({ variantId, selectedQuantity }) => ({ variantId, quantity: selectedQuantity }))

    setItemsToCancel(itemsToCancel)
  }, [selectableOffers])

  const getOrderCancellationReasons = () => {
    if (orderGroupedByShipmentsQuery.data?.salesChannel.type === 'TikTok') {
      return [
        { value: 'seller_cancel_reason_out_of_stock', label: 'Out of stock' },
        { value: 'seller_cancel_reason_wrong_price', label: 'Pricing error' },
        {
          value: 'seller_cancel_paid_reason_address_not_deliver',
          label: 'Unable to deliver to buyer address',
        },
        {
          value: 'seller_cancel_paid_reason_buyer_requested_cancellation',
          label: 'Buyer requested cancellation',
        },
      ]
    }
  }

  const cancelOrder = (
    productsContent: ReactNode,
    expectedCostSplit?: ExpectedCostSplit,
    reason?: string,
  ) => {
    cancelOrderMutation.mutate(
      [orderId, { itemsToCancel, restockCancelledItems: shouldRestock, reason, expectedCostSplit }],
      {
        onSuccess: () => {
          invalidateOrderCancellationQuery()
          navigate(reverse(routing.contributions.orders.order.self, { orderId }))
          Toasts.notify('Order has been canceled successfully.', { type: 'info' })
        },
        onError: (error: unknown) => {
          if (
            axios.isAxiosError(error) &&
            error.response?.data.code === 'ORDER_CANCEL_EXPECTED_CHARGE_AMOUNT_CHANGED'
          ) {
            open(OrderCancelConfirmModal, {
              orderId,
              itemsToCancel,
              content: productsContent,
              reasonOptions: getOrderCancellationReasons(),
              initialReason: reason,
              error: 'Your balance has changed and cannot cover refund amount. Please try again.',
              onConfirm: (expectedCostSplit, reason) => {
                cancelOrder(productsContent, expectedCostSplit, reason)
              },
            })

            return
          }
          Toasts.notify(getDefaultErrorMessage(error), { type: 'error' })
        },
      },
    )
  }

  const handleCancelOrder = (productsContent: ReactNode) => {
    open(OrderCancelConfirmModal, {
      orderId,
      itemsToCancel,
      content: productsContent,
      reasonOptions: getOrderCancellationReasons(),
      onConfirm: (expectedCostSplit, reason) =>
        cancelOrder(productsContent, expectedCostSplit, reason),
    })
  }

  const handleOfferSelect = (selectableOffer: SelectableOffer, selected: boolean) => {
    setSelectableOffers((currentSelectableOffers) =>
      currentSelectableOffers?.map((offer) => {
        if (selectableOffer.variantId !== offer.variantId) {
          return offer
        }

        return { ...offer, selected }
      }),
    )
  }

  const handleOfferSelectedQuantityChange = (
    selectableOffer: SelectableOffer,
    selectedQuantity: number,
  ) => {
    setSelectableOffers((currentSelectableOffers) =>
      currentSelectableOffers?.map((offer) => {
        if (selectableOffer.variantId !== offer.variantId) {
          return offer
        }

        return { ...offer, selectedQuantity }
      }),
    )
  }

  const handleSelectAll = (selected: boolean) => {
    setSelectableOffers((currentSelectableOffers) =>
      currentSelectableOffers?.map((offer) => {
        return { ...offer, selected }
      }),
    )
  }

  return (
    <OrderCancelView
      orderId={orderId}
      friendlyId={orderGroupedByShipmentsQuery.data?.friendlyId}
      cancelableOffers={selectableOffers}
      nonCancellableOffers={nonCancellableOffers}
      donation={orderGroupedByShipmentsQuery.data?.donation || undefined}
      promotion={orderGroupedByShipmentsQuery.data?.promotion}
      subtotal={calculateCancelRefunds?.cancellationAmounts.items}
      shipping={calculateCancelRefunds?.cancellationAmounts.shipping}
      taxes={calculateCancelRefunds?.cancellationAmounts.tax}
      discount={calculateCancelRefunds?.cancellationAmounts.discount}
      refundTotal={calculateCancelRefunds?.amountToRefund}
      shouldRestock={shouldRestock}
      isCancelButtonDisabled={
        !partiallyCancellable ||
        !selectableOffers?.filter((offer) => offer.selected).length ||
        calculateCancelRefundsQuery.isFetching ||
        calculateCancelRefundsQuery.isError ||
        orderCancellationQuery.isFetching ||
        orderCancellationQuery.isError
      }
      isLoading={
        orderGroupedByShipmentsQuery.isLoading ||
        calculateCancelRefundsQuery.isLoading ||
        calculateCancelRefundsQuery.isLoading ||
        cancelOrderMutation.isLoading
      }
      onOrderCancel={handleCancelOrder}
      onOfferSelect={handleOfferSelect}
      onRestockChange={setShouldRestock}
      onOfferSelectedQuantityChange={handleOfferSelectedQuantityChange}
      onSelectAll={handleSelectAll}
    />
  )
}
